import { InputNumber, Tooltip } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { Button, ConfirmBox, Export, FlexRow, toast, Typography } from "../../components";
import { COLORS } from "../../constants";
import { AppState } from "../../store";
import { useTrack } from "../../utils";

import { TabName } from "./WorkspaceAdmin";

const ListItem = styled.li`
  color: ${COLORS.black85};
`;

const DEFAULT_LAST_N_DAYS = 30;

type Props = {
  tab: TabName;
};

export const ExportCSVButton: React.FC<Props> = ({ tab }) => {
  const [lastNDays, setLastNDays] = useState<number>(DEFAULT_LAST_N_DAYS);
  const track = useTrack();

  const { search } = useLocation();

  const domain = new URLSearchParams(search).get("domain") || "all_domains";

  const [isModalOpen, setIsModalOpen] = useState(false);

  const domainsInDomainFamily = useSelector(
    (state: AppState) => state.app.domains_in_domain_family
  );

  const getCSVURL = ({ tab }: { tab: string }) => {
    const domainQueryString = domain === "all_domains" ? domainsInDomainFamily.toString() : domain;

    const host = `${window.location.protocol}//${window.location.hostname}/`;

    const searchParams = new URLSearchParams({
      domain: domainQueryString,
      tz_name: Intl.DateTimeFormat().resolvedOptions().timeZone,
      format: "csv",
    });

    if (tab === TabName.runs || tab === TabName.writeback_runs) {
      searchParams.set("last_n_days", String(lastNDays));
    }

    // Match the admin Dashboards list, which hides archived (owner-only) dashboards.
    if (tab === TabName.web_dashboards) {
      searchParams.set("hide_archived", "true");
    }

    const endpointsForTab: Partial<Record<TabName, string>> = {
      [TabName.runs]: "api_admin_ext/data_import_runs",
      [TabName.users]: "api_admin_ext/users",
      [TabName.alerts]: "api_admin_ext/alerts",
      [TabName.data_sources]: "api_admin_ext/data_sources",
      [TabName.writebacks]: "api_admin_ext/writebacks",
      [TabName.imports]: "api_admin_ext/data_imports",
      [TabName.writeback_runs]: "api_admin_ext/writeback_runs",
      [TabName.web_dashboards]: "api_admin_ext/web_dashboards",
    };

    return host + endpointsForTab[tab] + "?" + searchParams.toString();
  };

  return (
    <>
      <Tooltip title="Export as CSV">
        <Button type="default" onClick={() => setIsModalOpen(true)} className="gray">
          <Export />
        </Button>
      </Tooltip>

      {isModalOpen && (
        <ConfirmBox
          visible={isModalOpen}
          title="Export to a sheet using a CSV URL"
          cancelText="Cancel"
          okText="Copy CSV URL"
          okButton={
            <Button type="primary" style={{ marginBottom: 8 }}>
              Copy CSV URL
            </Button>
          }
          onCancel={() => {
            setIsModalOpen(false);
            setLastNDays(DEFAULT_LAST_N_DAYS);
          }}
          onOk={() => {
            navigator.clipboard.writeText(getCSVURL({ tab }));

            toast({ content: "Copied CSV URL to clipboard", type: "success" });

            setIsModalOpen(false);
            setLastNDays(DEFAULT_LAST_N_DAYS);

            track("workspace_admin_copy_csv_copied", {
              event_from: "workspace_admin",
              tab_name: tab,
              domain,
              platform: "webapp",
            });
          }}
          width={480}
          closable={true}
        >
          {tab === TabName.runs && (
            <FlexRow style={{ marginBottom: 12, alignItems: "center" }}>
              <Typography>Last</Typography>
              <InputNumber
                min={1}
                max={30}
                value={lastNDays}
                onChange={value => setLastNDays(value ?? DEFAULT_LAST_N_DAYS)}
                style={{ width: 80 }}
                size="small"
              />
              <Typography>days</Typography>
            </FlexRow>
          )}

          <ListItem>1. Copy the CSV URL below</ListItem>

          <ListItem>
            2. From the Coefficient sidebar, click <b>Import From</b>
          </ListItem>

          <ListItem>
            3. Click <b>CSV</b>
          </ListItem>

          <ListItem>
            4. Click <b>From URL</b>
          </ListItem>

          <ListItem>
            5. Paste the CSV URL link and click <b>Next</b> and then <b>Import</b>
          </ListItem>
        </ConfirmBox>
      )}
    </>
  );
};
